// src/app/components/chat/ChatComposer.jsx
import React, { useState, useRef } from "react";
import {
  Send,
  Paperclip,
  Image as ImageIcon,
  Mic,
  Smile,
  X,
  StopCircle,
} from "lucide-react";

export default function ChatComposer({
  disabled = false,
  onSendText,
  onSendGifUrl,
  onSendFile,
  onSendVoiceBlob,
}) {
  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearch, setGifSearch] = useState("");

  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Send text message
  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;

    onSendText(trimmed);
    setText("");
  };

  // Handle Enter key (Shift+Enter for new line, Enter to send)
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // File upload
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || disabled) return;

    // Max 10MB
    if (file.size > 10 * 1024 * 1024) {
      alert("File too large. Maximum 10MB.");
      return;
    }

    onSendFile(file);
    e.target.value = ""; // Reset input
  };

  // Voice recording
  const startRecording = async () => {
    if (disabled || !navigator.mediaDevices) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        onSendVoiceBlob(blob);

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Failed to start recording:", error);
      alert("Microphone access denied or unavailable.");
    }
  };

  const stopRecording = () => {
    if (!mediaRecorderRef.current) return;

    mediaRecorderRef.current.stop();
    setIsRecording(false);
    mediaRecorderRef.current = null;
  };

  // Simple GIF picker (you can integrate Giphy API later)
  const handleGifSelect = (gifUrl) => {
    if (disabled) return;
    onSendGifUrl(gifUrl);
    setShowGifPicker(false);
    setGifSearch("");
  };

  // Sample GIFs (replace with Giphy API in production)
  const sampleGifs = [
    "https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif",
    "https://media.giphy.com/media/l0MYt5jPR6QX5pnqM/giphy.gif",
    "https://media.giphy.com/media/3o6Zt6KHxJTbXCnSvu/giphy.gif",
  ];

  return (
    <div className="border-t border-gray-800 bg-gray-900 p-4">
      {/* GIF Picker Modal */}
      {showGifPicker && (
        <div className="mb-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Select GIF</h3>
            <button
              onClick={() => setShowGifPicker(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <input
            type="text"
            placeholder="Search GIFs..."
            value={gifSearch}
            onChange={(e) => setGifSearch(e.target.value)}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm mb-3 focus:outline-none focus:border-primary-500"
          />

          <div className="grid grid-cols-3 gap-2">
            {sampleGifs.map((url, i) => (
              <button
                key={i}
                onClick={() => handleGifSelect(url)}
                className="aspect-square rounded-lg overflow-hidden hover:opacity-75 transition-opacity"
              >
                <img src={url} alt={`GIF ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          <p className="text-xs text-gray-500 mt-3">
            💡 Tip: Integrate Giphy API for real GIF search
          </p>
        </div>
      )}

      {/* Voice Recording Indicator */}
      {isRecording && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse"></div>
            <span className="text-sm text-red-400 font-medium">Recording...</span>
          </div>
          <button
            onClick={stopRecording}
            className="flex items-center gap-2 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <StopCircle size={16} />
            Stop
          </button>
        </div>
      )}

      {/* Input Area */}
      <div className="flex items-end gap-2">
        {/* Attachment Buttons */}
        <div className="flex items-center gap-1">
          {/* File Upload */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="p-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Attach file"
          >
            <Paperclip size={20} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
          />

          {/* GIF Picker */}
          <button
            onClick={() => setShowGifPicker(!showGifPicker)}
            disabled={disabled}
            className="p-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Send GIF"
          >
            <ImageIcon size={20} />
          </button>

          {/* Voice Recording */}
          <button
            onClick={isRecording ? stopRecording : startRecording}
            disabled={disabled}
            className={`p-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isRecording
                ? "bg-red-500 hover:bg-red-600 text-white"
                : "bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white"
            }`}
            title={isRecording ? "Stop recording" : "Record voice message"}
          >
            <Mic size={20} />
          </button>
        </div>

        {/* Text Input */}
        <div className="flex-1 relative">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={disabled ? "Loading..." : "Type a message... (Shift+Enter for new line)"}
            rows={1}
            className="w-full px-4 py-3 pr-12 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 resize-none focus:outline-none focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              minHeight: "48px",
              maxHeight: "120px",
              overflowY: text.split("\n").length > 2 ? "auto" : "hidden",
            }}
          />

          {/* Character Count (optional) */}
          {text.length > 0 && (
            <div className="absolute bottom-2 right-2 text-xs text-gray-500">
              {text.length}/2000
            </div>
          )}
        </div>

        {/* Send Button */}
        <button
          onClick={handleSend}
          disabled={disabled || !text.trim()}
          className="p-3 rounded-lg bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-primary-600 disabled:hover:to-primary-500 shadow-lg shadow-primary-500/20"
          title="Send message (Enter)"
        >
          <Send size={20} />
        </button>
      </div>

      {/* Help Text */}
      <div className="mt-2 text-xs text-gray-500 flex items-center justify-between">
        <span>Press Enter to send, Shift+Enter for new line</span>
        <span>Max file size: 10MB</span>
      </div>
    </div>
  );
}
